import React, { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';

import CheckboxInput from 'CommonComponents/CheckboxInput';
import {
    collectLeafIds,
    filterNodes,
    getParentState,
    isAllSelected,
    leafIdsOfParent,
    toggleLeaf,
    toggleParent,
} from 'CommonComponents/TreeMultiSelect/helpers';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import XMarkIcon from 'Assets/icons/xmark.svg';

const isRtl = () => typeof document !== 'undefined' && document.documentElement.dir === 'rtl';

function defaultSummarise(value, nodes) {
    const selectedSet = new Set(value);
    const allLeafIds = new Set(collectLeafIds(nodes));

    let groups = 0;
    (nodes || []).forEach((group) => {
        (group.parents || []).forEach((parent) => {
            if (leafIdsOfParent(parent).some((id) => selectedSet.has(id))) {
                groups += 1;
            }
        });
    });

    const items = value.filter((id) => allLeafIds.has(id)).length;

    return $t('{{groups}} groups, {{items}} items selected', { groups, items });
}

const defaultChipLabel = (node, parent) => (parent ? `${parent.label} / ${node.label}` : node.label);

// Row expanders read as a disclosure triangle: sideways when collapsed, down when open.
function Caret({ expanded }) {
    const rotation = expanded ? '' : isRtl() ? 'tw-rotate-90' : '-tw-rotate-90';

    return <ChevronDownIcon className={classNames('tw-w-3.5 tw-h-3.5 tw-transition-transform', rotation)} />;
}

// The trigger reads as a select: down when closed, flipped up while the panel is open.
function TriggerCaret({ open }) {
    return <ChevronDownIcon className={classNames('tw-w-3.5 tw-h-3.5 tw-transition-transform', open && 'tw-rotate-180')} />;
}

function Chip({ label, onRemove, disabled }) {
    return (
        <span className="tw-inline-flex tw-items-center tw-gap-x-1.5 tw-rounded-[14px] tw-bg-blue-25 tw-text-charcoal tw-text-[11.5px] tw-font-medium tw-px-2.5 tw-py-1">
            <span className="tw-truncate tw-max-w-[180px]">{label}</span>
            {!disabled && (
                <button
                    type="button"
                    className="tw-flex tw-items-center tw-text-blue hover:tw-text-dark-blue"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    aria-label={$t('Remove')}
                >
                    <XMarkIcon className="tw-w-2 tw-h-2" />
                </button>
            )}
        </span>
    );
}

/**
 * Generic controlled tri-state hierarchical multiselect. Knows nothing about any
 * particular domain — see the data model documented in `helpers.js`.
 */
export default function TreeMultiSelect({
    nodes = [],
    value = [],
    onChange,
    summarise = defaultSummarise,
    chipLabel = defaultChipLabel,
    searchPlaceholder,
    allLabel = $t('All'),
    disabled = false,
    renderTrigger,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [manuallyExpanded, setManuallyExpanded] = useState(() => new Set());
    const wrapperRef = useRef(null);

    const selectedSet = useMemo(() => new Set(value), [value]);
    const allSelected = useMemo(() => isAllSelected(value, nodes), [value, nodes]);
    const { groups: filteredGroups, autoExpand } = useMemo(() => filterNodes(nodes, search), [nodes, search]);

    const close = () => {
        setIsOpen(false);
        setSearch('');
    };

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const onDocMouseDown = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                close();
            }
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                close();
            }
        };

        document.addEventListener('mousedown', onDocMouseDown, true);
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('mousedown', onDocMouseDown, true);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen]);

    const isExpanded = (parentId) => (search ? autoExpand.has(parentId) : manuallyExpanded.has(parentId));

    const toggleExpansion = (parentId) => {
        setManuallyExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(parentId)) {
                next.delete(parentId);
            } else {
                next.add(parentId);
            }
            return next;
        });
    };

    const chips = useMemo(() => {
        const result = [];
        (nodes || []).forEach((group) => {
            (group.parents || []).forEach((parent) => {
                const state = getParentState(parent, selectedSet);
                if (state === 'checked') {
                    result.push({ key: parent.id, label: chipLabel(parent), remove: () => onChange(toggleParent(value, parent)) });
                } else if (state === 'indeterminate') {
                    (parent.children || []).forEach((child) => {
                        if (selectedSet.has(child.id)) {
                            result.push({ key: child.id, label: chipLabel(child, parent), remove: () => onChange(toggleLeaf(value, child.id)) });
                        }
                    });
                }
            });
        });
        return result;
    }, [nodes, selectedSet, value, onChange, chipLabel]);

    const summaryText = allSelected ? allLabel : summarise(value, nodes);

    // The summary alone sits in the trigger; the current selection is spelled out
    // in chips underneath it, so a long selection never inflates the control.
    const triggerContent = renderTrigger ? (
        renderTrigger({ allSelected, summaryText, chips, isOpen })
    ) : (
        <span className="tw-text-[13.5px] tw-font-normal tw-text-charcoal">{summaryText}</span>
    );

    return (
        <div className="tw-relative" ref={wrapperRef}>
            {/* role=button rather than a real <button> so the removable chips
                (which contain their own <button>) are not nested inside a button. */}
            <div
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-disabled={disabled || undefined}
                onClick={() => {
                    if (!disabled) {
                        isOpen ? close() : setIsOpen(true);
                    }
                }}
                onKeyDown={(e) => {
                    if (disabled || (e.key !== 'Enter' && e.key !== ' ')) {
                        return;
                    }
                    e.preventDefault();
                    isOpen ? close() : setIsOpen(true);
                }}
                className={classNames(
                    'tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-x-2 tw-rounded-[9px] tw-bg-grey/50 tw-px-3 tw-py-2.5 tw-text-start tw-text-charcoal-50',
                    disabled ? 'tw-opacity-60 tw-cursor-not-allowed' : 'tw-cursor-pointer',
                )}
            >
                <span className="tw-flex-1 tw-min-w-0 tw-truncate">{triggerContent}</span>
                <TriggerCaret open={isOpen} />
            </div>

            {isOpen && (
                <div className="tw-absolute tw-z-[9999] tw-mt-2 tw-w-full tw-max-w-[560px] tw-min-w-[280px] tw-rounded-xl tw-border tw-border-grey tw-bg-white tw-shadow-lg">
                    <div className="tw-flex tw-items-center tw-gap-x-2.5 tw-px-3.5 tw-py-3 tw-border-b tw-border-grey">
                        <input
                            type="text"
                            autoFocus
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={searchPlaceholder || $t('Search')}
                            className="tw-flex-1 tw-min-w-0 tw-rounded-lg tw-bg-grey/50 tw-px-3 tw-py-2 tw-text-[12.5px] tw-border-0 focus:tw-ring-2 focus:tw-ring-blue-50"
                        />
                        <button
                            type="button"
                            className="tw-whitespace-nowrap tw-text-xs tw-font-medium tw-text-blue hover:tw-underline"
                            onClick={() => onChange(collectLeafIds(nodes))}
                        >
                            {$t('Select all')}
                        </button>
                        <button
                            type="button"
                            className="tw-whitespace-nowrap tw-text-xs tw-font-medium tw-text-blue hover:tw-underline"
                            onClick={() => onChange([])}
                        >
                            {$t('Clear')}
                        </button>
                    </div>

                    <div className="tw-max-h-[290px] tw-overflow-y-auto tw-py-2">
                        {filteredGroups.length === 0 && <div className="tw-px-4 tw-py-3 tw-text-sm tw-text-charcoal-50">{$t('No results')}</div>}

                        {filteredGroups.map((group) => (
                            <div key={group.key} className="tw-px-1.5 tw-pt-1.5">
                                <div className="tw-px-2.5 tw-py-1.5 tw-text-[11.5px] tw-font-semibold tw-uppercase tw-tracking-[0.6px] tw-text-charcoal-50">
                                    {group.label}
                                </div>

                                {group.parents.map((parent) => {
                                    const hasChildren = parent.children && parent.children.length > 0;
                                    const state = getParentState(parent, selectedSet);
                                    const expanded = isExpanded(parent.id);

                                    return (
                                        <div key={parent.id}>
                                            <div className="tw-flex tw-items-center tw-gap-x-2 tw-rounded-[7px] tw-px-2.5 tw-py-1.5 tw-text-[13px] tw-font-medium hover:tw-bg-background">
                                                {hasChildren ? (
                                                    <button
                                                        type="button"
                                                        className="tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-text-charcoal-50"
                                                        onClick={() => toggleExpansion(parent.id)}
                                                        aria-expanded={expanded}
                                                        aria-label={expanded ? $t('Collapse') : $t('Expand')}
                                                    >
                                                        <Caret expanded={expanded} />
                                                    </button>
                                                ) : (
                                                    <span className="tw-inline-block tw-w-4" />
                                                )}
                                                <CheckboxInput
                                                    label={parent.label}
                                                    checked={state === 'checked'}
                                                    indeterminate={state === 'indeterminate'}
                                                    disabled={disabled}
                                                    onChange={() => onChange(toggleParent(value, parent))}
                                                />
                                            </div>

                                            {hasChildren &&
                                                expanded &&
                                                parent.children.map((child) => (
                                                    <div
                                                        key={child.id}
                                                        className="tw-flex tw-items-center tw-rounded-[7px] tw-py-1.5 tw-pe-2.5 tw-ps-11 tw-text-[13px] tw-font-normal hover:tw-bg-background"
                                                    >
                                                        <CheckboxInput
                                                            label={child.label}
                                                            checked={selectedSet.has(child.id)}
                                                            disabled={disabled}
                                                            onChange={() => onChange(toggleLeaf(value, child.id))}
                                                        />
                                                    </div>
                                                ))}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!allSelected && chips.length > 0 && (
                <div className="tw-mt-2.5 tw-flex tw-flex-wrap tw-gap-1.5">
                    {chips.map((chip) => (
                        <Chip key={chip.key} label={chip.label} onRemove={chip.remove} disabled={disabled} />
                    ))}
                </div>
            )}
        </div>
    );
}
